import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  type ButtonProps,
  CircularProgress,
  Typography,
} from '@mui/material';
import { DataGrid } from '@unicorn-shared/interface';
import { Actions } from '@shared/ui';
import { columns, type ImportErrorRow } from './model';

export type Props = {
  isOpen: boolean;
  title?: string;
  isValidating: boolean;
  successText: string;
  errors: ImportErrorRow[];
  onConfirm: NonNullable<ButtonProps['onClick']>;
  onImportNewFile: NonNullable<ButtonProps['onClick']>;
  onClose: NonNullable<ButtonProps['onClick']>;
};

export const ImportDialog: FC<Props> = ({
  isOpen,
  title,
  isValidating,
  successText,
  errors = [],
  onConfirm,
  onImportNewFile,
  onClose,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth={!isValidating && errors.length > 0 ? 'xl' : 'xs'}
    >
      <DialogTitle>{title || t('ImportingData')}</DialogTitle>
      <DialogContent>
        {isValidating ? (
          <Actions>
            <CircularProgress size={16} />
            <Typography variant="body1">{t('CheckInProgress')}</Typography>
          </Actions>
        ) : (
          <DialogContentText color="textPrimary">
            {errors.length > 0 ? (
              <div>
                <Typography variant="body1" sx={{ marginBottom: '24px' }}>
                  {`${t('TheFollowingErrorsWereEncounteredWhileImportingData')}:`}
                </Typography>
                <DataGrid columns={columns} rows={errors} showPagination={false} />
              </div>
            ) : (
              successText
            )}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        {!isValidating && errors.length === 0 && successText && (
          <Button variant="contained" onClick={onConfirm}>
            {t('Confirm')}
          </Button>
        )}
        {!isValidating && errors.length > 0 && (
          <Button variant="outlined" onClick={onImportNewFile}>
            {t('ImportNewFile')}
          </Button>
        )}
        <Button variant="outlined" onClick={onClose}>
          {t('cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
